import React, { useState, useMemo, useCallback, Fragment, ReactNode } from 'react';
import useLabels from '../../hooks/useLabels';
import AvatarIcon from '../avatar-icon/AvatarIcon';
import { Icon } from '../icon/Icon';
import { useAuth } from '../../contexts/auth/AuthContext';
import { DfContextMenu, DfMenuItem } from '@danfoss/mosaic-react';
import { getClassNames } from '../../helpers/classHelpers';
import { Link } from 'react-router-dom';
import { AppRoute } from '../../router/Routing';
import { wrapInOptionalLink } from '../../helpers/linkHelpers';
import { IconStyles, IconTheme } from '../icon/Icon.types';
import { Icons } from '../icon/icons/material';
import { SvgIcons } from '../icon/icons/svg';
import style from './Header.module.scss';
import runtimeConfig from '../../config/runtimeConfig';

const viewRequestUrl = `${runtimeConfig.powerSourceUrl}/my-requests`;

export type HeaderProps = {
    navItems?: NavItem[];
    userDropdownItems?: UserDropdownItem[];
    className?: string;
};

type UserDropdownItem = Omit<NavItem, 'className' | 'icon'>;
type NavItem = {
    label: string;
    url?: string;
    target?: string;
    rel?: string;
    icon?: ReactNode;
    isHidden?: boolean;
    /** Default: `true` */
    closeOnClick?: boolean;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    className?: string;
};

const Header: React.FC<HeaderProps> = ({ navItems, userDropdownItems, className }) => {
    const { user, logout } = useAuth();
    const labels = useLabels();

    const l = useMemo(() => {
        return {
            changeLang: labels.changeLanguage,
            lo: labels.logout,
            darkMode: labels.darkMode,
            lightMode: labels.lightMode
        };
    }, [labels.changeLanguage, labels.logout, labels.darkMode, labels.lightMode]);

    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

    const renderNavItem = useCallback(({ label, icon, onClick, url, className, target, rel }: NavItem, i?: number) => {
        const inner = <>
            {icon}
            <span className={getClassNames([!!icon && 'df-hidden-xs', 'df-link-text'])}>{label}</span>
        </>;

        const isExternal = !!url && (/^https?:\/\//i.test(url) || target === '_blank');

        const content = !url
            ? <div>{inner}</div>
            : isExternal
                ? <a href={url} className='df-link-tertiary' onClick={onClick} target={target} rel={rel} aria-label={label}>{inner}</a>
                : <Link to={url} className='df-link-tertiary' onClick={onClick} target={target} aria-label={label}>{inner}</Link>;

        return <li slot='link' className={getClassNames([className])} key={i}>
            {content}
        </li>;
    }, []);

    const renderUserDropdownItem = useCallback(({ label, onClick, url, target, closeOnClick = true, isHidden }: UserDropdownItem, i?: number) => {
        const onMenuItemClick = closeOnClick ? () => setIsContextMenuOpen(false) : undefined;
        const inner = <div className={getClassNames([isHidden && style.hidden])}><DfMenuItem onClick={onMenuItemClick} label={label} /></div>;
        return <Fragment key={i}>{wrapInOptionalLink(inner, url, { className: getClassNames(['df-link-tertiary', isHidden && style.hidden]), onClick, target, })}</Fragment>;
    }, []);

    const onUserClickHandler = useCallback(() => {
        setIsContextMenuOpen(true);
    }, []);

    const userIcon = useMemo(() => {
        if (!user) return null;
        const imageUrl = user.avatar;
        if (imageUrl) return <AvatarIcon borderWidth={1} borderColor='white' color='white' imageUrl={imageUrl} className='df-addon-start' />;
        return <Icon.Svg title='' size={16} borderSize={1} borderColor='white' fill='white' iconName={SvgIcons.User} className='df-addon-start' />;
    }, [user]);


    const renderLogo = useMemo(() => {
        return (
            <div className='df-logo-container'>
                <Link to={AppRoute.chat} className={getClassNames(['df-logo', style['logo-override']])} aria-label='Home page' >
                    <picture>
                        <source
                            srcSet='https://cdn.mosaic.danfoss.com/npm/mosaic-assets@0.5.1/dist/logos/danfoss-2025-mobile.svg'
                            media='(max-width: 599.5px)'
                        />
                        <img
                            src='https://cdn.mosaic.danfoss.com/npm/mosaic-assets@0.5.1/dist/logos/danfoss-2025.svg'
                            alt='Danfoss logo'
                        />
                    </picture>
                </Link>
            </div>
        );
    }, []);

    return (
        <header className={getClassNames(['df-header', style.header, className])}>
            <div className='df-container df-header-container'>

                {renderLogo}

                <nav className='header-nav-container' aria-label='header links'>
                    <ul className={style.list}>
                        {navItems?.map(renderNavItem)}

                        {user && <div className={'df-context-menu-container'}>
                            {renderNavItem({
                                label: user.name || user.username,
                                onClick: onUserClickHandler,
                                icon: user.avatar && userIcon,
                                className: getClassNames([style.user, 'df-context-menu-button']),
                            })}
                            <DfContextMenu
                                onOutsideClick={() => setIsContextMenuOpen(false)}
                                isRightAsXPosition
                                posY={38}
                                posX={-38}
                                isOpen={isContextMenuOpen}
                            >
                                {userDropdownItems?.map(renderUserDropdownItem)}

                                <DfMenuItem onClick={logout} className='df-color-error' label={l.lo} />
                            </DfContextMenu>
                        </div>}

                        {user && <Icon.Base
                            className='menu-icon'
                            title={labels.navigateToInformationViewIconTitle}
                            iconName={Icons.menu}
                            theme={IconTheme.light}
                            onClick={onUserClickHandler}
                        />}
                    </ul>
                </nav>

            </div>
        </header>
    );
};

export default Header;